import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

const AdminLogin = () => {
  const { user, loading, login, formatApiErrorDetail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!loading && user?.role === "admin") return <Navigate to="/admin/panel" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("¡Bienvenido, jefe de sala!");
      navigate("/admin/panel");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-xl border border-edge bg-ink2 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none transition-colors";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink p-5">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-crimson/20 blur-[130px]" />
      <div className="glass relative w-full max-w-md rounded-3xl p-8 md:p-10">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <LogoMark className="h-14 w-14" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-white">Zona privada</h1>
          <p className="text-sm text-slate-400">Acceso solo para el administrador de ENOLFC.</p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <input
            required
            type="email"
            data-testid="admin-login-email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
          />
          <input
            required
            type="password"
            data-testid="admin-login-password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={field}
          />
          {error && (
            <p className="rounded-lg border border-crimson/40 bg-crimson/10 px-4 py-2.5 text-sm text-glow" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-crimson py-3.5 font-display text-lg font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
          >
            <Lock className="h-4 w-4" /> {busy ? "Entrando..." : "Entrar"}
          </button>
        </form>
        <a href="/" data-testid="admin-login-back" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Volver a la web
        </a>
      </div>
    </div>
  );
};

export default AdminLogin;
